import { structureQueryUrlV1 } from '@dbmx/sdmx';
import { has, indexBy, isEmpty, isNil, omit, pathOr } from '../../utils/fp';
import * as rules2 from '../rules2';
import { getStructureV1HeaderFormat } from '../headers';
import { getDataStructureAnnotations, getDataflowAnnotations } from './accessors/structure';
import { sortByProp } from '../../utils/sort';

export const getHierarchicalCodelistsReferences = sdmxJson => {
  const dsdReferences = rules2.getHCodelistsRefs(getDataStructureAnnotations(sdmxJson));
  const dataflowReferences = rules2.getHCodelistsRefs(getDataflowAnnotations(sdmxJson));

  return { ...dsdReferences, ...dataflowReferences };
};

// The request of the hierarchical codelist a reference points to, on the datasource
// of the dataflow and in the page locale.
export const getHierarchicalCodelistRequestArgs =
  (datasource, locale) =>
  ({ agencyId, code, version }) => ({
    url: structureQueryUrlV1({
      agencyID: agencyId,
      id: code,
      version,
      endpoint: datasource?.url,
      resourceType: 'hierarchicalcodelist',
      format: 'json',
    }),
    headers: {
      Accept: getStructureV1HeaderFormat(datasource),
      'Accept-Language': locale,
    },
  });

// The codes of one hierarchy of a hierarchical codelist response.
export const getHierarchyCodes = (hcl, hierarchy) => {
  const hierarchies = pathOr([], ['data', 'hierarchicalCodelists', 0, 'hierarchies'], hcl);
  const found = hierarchies.find(item => item.id === hierarchy);
  return isNil(found) ? [] : found.hierarchicalCodes || [];
};

export const getMultiHierarchicalFilters = (filters, hierarchies) =>
  filters.map(dimension => {
    const indexedValues = indexBy(value => value.id, dimension.values || []);
    if (!has(dimension.id, hierarchies)) {
      return {
        ...dimension,
        values: (dimension.values || []).map(value => ({
          ...value,
          parentId: has(value.parentId, indexedValues) ? value.parentId : undefined,
        })),
      };
    }
    const hierarchy = rules2.refinePartialHierarchy(hierarchies[dimension.id], indexedValues);

    let rest = indexedValues;
    const hierarchyValues = Object.keys(hierarchy).reduce((acc, parentKey) => {
      const ids = hierarchy[parentKey];
      rest = omit(ids, rest);
      const parentId = parentKey === '#ROOT' ? undefined : parentKey;
      const values = ids
        .map(id => indexedValues[id])
        .filter(value => !isNil(value))
        .map(val => ({
          ...val,
          hierarchicalId: isNil(parentId) ? val.id : `${parentId}.${val.id}`,
          parentId,
        }));

      return [...acc, ...values];
    }, []);

    let values = hierarchyValues;
    if (!isEmpty(rest)) {
      const evolved = sortByProp('position')(
        Object.values(rest).map(value => ({ ...value, parentId: undefined })),
      );
      values = [...evolved, ...hierarchyValues];
    }
    return { ...dimension, values };
  });
